import React from 'react';
import { isAuthorizationError, isAuthenticationError, clearAuthData } from '../utils/authUtils';
import { Box, Typography, Button, Alert } from '@mui/material';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            isAuthError: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        const isAuthError = isAuthorizationError(error) || isAuthenticationError(error);

        return {
            hasError: true,
            error,
            isAuthError
        };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            errorInfo
        });

        // Handle specific authorization errors
        if (isAuthenticationError(error)) {
            console.log('Authentication error detected, clearing auth data');
            clearAuthData();

            // Redirect to login after a short delay
            setTimeout(() => {
                window.location.href = '/sign-in';
            }, 2000);
        } else if (isAuthorizationError(error)) {
            console.log('Authorization error detected');

            // Redirect to dashboard after a short delay
            setTimeout(() => {
                window.location.href = '/dashboard';
            }, 3000);
        }

        // You can also log the error to an error reporting service here
        // logErrorToService(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            isAuthError: false
        });
    };

    handleGoToDashboard = () => {
        window.location.href = '/dashboard';
    };

    handleGoToLogin = () => {
        window.location.href = '/sign-in';
    };

    render() {
        if (this.state.hasError) {
            const { error, isAuthError } = this.state;

            // Authentication error UI
            if (isAuthenticationError(error)) {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
                        <Box sx={{ maxWidth: 'md', width: '100%', textAlign: 'center' }}>
                            <div className="text-center">
                                <div className="mx-auto h-12 w-12 text-red-400">
                                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <Typography variant="h2" component="h2" sx={{ mt: 6, fontWeight: 'extrabold', color: 'text.primary' }}>
                                    Session Expired
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Your session has expired. Please log in again to continue.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    You will be redirected to the login page automatically.
                                </Typography>
                            </div>
                            <div className="mt-8 space-y-3">
                                <Button
                                    onClick={this.handleGoToLogin}
                                    variant="contained"
                                    color="primary"
                                    sx={{ width: '100%', mt: 2 }}
                                >
                                    Go to Login
                                </Button>
                                {process.env.NODE_ENV === 'development' && (
                                    <Button
                                        onClick={this.handleRetry}
                                        variant="outlined"
                                        color="primary"
                                        sx={{ width: '100%', mt: 2 }}
                                    >
                                        Retry (Dev Only)
                                    </Button>
                                )}
                            </div>
                        </Box>
                    </Box>
                );
            }

            // Authorization error UI
            if (isAuthorizationError(error)) {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
                        <Box sx={{ maxWidth: 'md', width: '100%', textAlign: 'center' }}>
                            <div className="text-center">
                                <div className="mx-auto h-12 w-12 text-red-400">
                                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                </div>
                                <Typography variant="h2" component="h2" sx={{ mt: 6, fontWeight: 'extrabold', color: 'text.primary' }}>
                                    Access Denied
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    You do not have permission to access this resource.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    You will be redirected to your dashboard automatically.
                                </Typography>
                            </div>
                            <div className="mt-8 space-y-3">
                                <Button
                                    onClick={this.handleGoToDashboard}
                                    variant="contained"
                                    color="primary"
                                    sx={{ width: '100%', mt: 2 }}
                                >
                                    Return to Dashboard
                                </Button>
                                {process.env.NODE_ENV === 'development' && (
                                    <Button
                                        onClick={this.handleRetry}
                                        variant="outlined"
                                        color="primary"
                                        sx={{ width: '100%', mt: 2 }}
                                    >
                                        Retry (Dev Only)
                                    </Button>
                                )}
                            </div>
                        </Box>
                    </Box>
                );
            }

            // Generic error UI
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
                    <Box sx={{ maxWidth: 'md', width: '100%', textAlign: 'center' }}>
                        <div className="text-center">
                            <div className="mx-auto h-12 w-12 text-red-400">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <Typography variant="h2" component="h2" sx={{ mt: 6, fontWeight: 'extrabold', color: 'text.primary' }}>
                                Something went wrong
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                An unexpected error occurred. Please try again.
                            </Typography>
                            {process.env.NODE_ENV === 'development' && (
                                <details className="mt-4 text-left">
                                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                        Error Details (Dev Only)
                                    </summary>
                                    <pre className="mt-2 text-xs text-red-600 whitespace-pre-wrap bg-red-50 p-2 rounded">
                                        {error?.toString()}
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </details>
                            )}
                        </div>
                        <div className="mt-8 space-y-3">
                            <Button
                                onClick={this.handleRetry}
                                variant="contained"
                                color="primary"
                                sx={{ width: '100%', mt: 2 }}
                            >
                                Try Again
                            </Button>
                            <Button
                                onClick={this.handleGoToDashboard}
                                variant="outlined"
                                color="primary"
                                sx={{ width: '100%', mt: 2 }}
                            >
                                Return to Dashboard
                            </Button>
                        </div>
                    </Box>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 